import { listPackages, getPackage } from '../../api/subscriptions/subscriptions';
import { computeAnnualPrice } from '../../packages/utils/packagePricing';
import { pricingPackages as fallbackPricingPackages } from '../components/pricing/pricingData';
import { isContactSalesPackage } from '../utils/contactSalesPackage';

function mapFeatures(features) {
  if (Array.isArray(features)) {
    return features.filter(Boolean).map((name, index) => ({
      id: String(index + 1),
      name,
      included: true,
    }));
  }

  if (features && typeof features === 'object') {
    return Object.entries(features)
      .filter(([, enabled]) => enabled)
      .map(([key], index) => ({
        id: key,
        name: key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' '),
        included: true,
      }));
  }

  return [];
}

export function mapFirestorePackageToPricingCard(pkg) {
  const contactOnly = isContactSalesPackage(pkg);
  const monthlyPrice = contactOnly ? 0 : parseFloat(String(pkg.price?.monthly ?? 0));
  const annualDiscount = pkg.subscription?.annualDiscount ?? 0;
  const yearlyPrice = contactOnly ? 0 : computeAnnualPrice(monthlyPrice, annualDiscount);
  const routeId = pkg.slug || pkg.id;

  return {
    id: routeId,
    firestoreId: pkg.id,
    slug: pkg.slug,
    name: contactOnly ? 'Custom' : pkg.name,
    monthlyPrice,
    yearlyPrice,
    description: contactOnly
      ? (pkg.description || 'Tailored solutions for larger teams and unique requirements.')
      : (pkg.description || ''),
    isPopular: !!pkg.isPopular,
    features: mapFeatures(pkg.features),
    buttonText: contactOnly ? 'Contact Us' : 'Get Started',
    contactOnly,
    limits: pkg.limits,
    subscription: pkg.subscription,
    isTrialPackage: !!pkg.isTrialPackage,
  };
}

function comparePublicPackageOrder(a, b) {
  if (a.contactOnly !== b.contactOnly) {
    return a.contactOnly ? 1 : -1;
  }
  return a.monthlyPrice - b.monthlyPrice;
}

export async function fetchPublicPackages() {
  try {
    const packages = (await listPackages())
      .filter((pkg) => pkg.isActive && !pkg.isAdminPackage && !pkg.isTrialPackage)
      .map(mapFirestorePackageToPricingCard)
      .sort(comparePublicPackageOrder);
    return packages.length > 0 ? packages : fallbackPricingPackages;
  } catch (error) {
    console.error('Error fetching public packages:', error);
    return fallbackPricingPackages;
  }
}

export async function fetchTrialPackage() {
  try {
    const packages = await listPackages();
    return packages.find((pkg) => pkg.isTrialPackage === true && pkg.isActive) || null;
  } catch (error) {
    console.error('Error fetching trial package:', error);
    return null;
  }
}

export { getPackage, listPackages };
